import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NguCarousel, NguCarouselConfig } from '@ngu/carousel';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '@spartacus/asm/root';
import { BaseSiteService, CmsService, OccEndpointsService, RoutingService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Subscription, race, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CustomProdPgService } from 'src/app/custom-prod-pg/custom-prod-pg.service';
import { POC_NEW_LOGIN_PAGE } from 'src/app/shared/modules/release-banner/constants';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { CustomAuthService } from '../../../custom-auth-service.service';

@Component({
    selector: 'app-product-carousel-homepg',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './product-carousel-homepg.component.html',
    styleUrls: ['./product-carousel-homepg.component.scss'],
    standalone: false
})
export class ProductCarouselHomepgComponent implements OnInit, OnDestroy {
  @ViewChild('myCarousel') myCarousel: NguCarousel<any>;

  carouselConfig: NguCarouselConfig = {
    grid: {
      xs: 1,
      sm: 2,
      md: 2,
      lg: 3,
      all: 0,
    },
    load: 3,
    interval: {
      timing: 4000,
      initialDelay: 1000,
    },
    loop: false,
    touch: true,
    velocity: 0.2,
    slide: 3,
    point: {
      visible: true,
      hideOnSingleSlide: true,
    },
  };
  public categoryList = [];
  baseUrl: any;
  fallbackImage = 'i18n-assets/sanofi_missing_product.jpg';
  slideNo = 0;
  imagesFormats: string;
  public images: any;
  categoryDetails: any;
  catEntries: any;
  categoryURL: any;
  baseSiteId: any;
  authSub: Subscription;
  flagSub: Subscription;
  baseSub: Subscription;
  categorySub: Subscription;

  constructor(
    private readonly cmsService: CmsService,
    private readonly occService: OccEndpointsService,
    private readonly customProdService: CustomProdPgService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
    private readonly baseSiteService: BaseSiteService,
    private readonly route: RoutingService,
    private readonly user: UserAccountFacade,
    private readonly authService: CustomAuthService,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    this.authSub = this.authService
      .isUserLoggedIn()
      .pipe(take(1))
      .subscribe((loggedInRes: any) => {
        if (!loggedInRes) {
          return;
        }

        // Don't auto-redirect ASM agents impersonating a user.
        const asmEnabled = localStorage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
        if (asmEnabled) {
          return;
        }

        if (window.location.pathname.includes('/homepage')) {
          this.router.navigateByUrl('my-account/account-overview');
          return;
        }

        // Category PLP and other Section3 pages: wait for the flag before deciding.
        // Flag off → legacy redirect; flag on → preserve deep-link returnUrl.
        this.flagSub = race(
          this.featureFlagService.getFlagObservable(POC_NEW_LOGIN_PAGE, false).pipe(take(1)),
          timer(1500).pipe(
            map(() =>
              this.featureFlagService.getBooleanFlagIsEnabled(POC_NEW_LOGIN_PAGE, false)
            )
          )
        ).subscribe((flag) => {
            if (flag !== true) {
              this.router.navigateByUrl('my-account/account-overview');
            }
          });
      });
  }
  ngOnInit(): void {
    this.baseUrl = this.occService.getBaseUrl({
      baseUrl: true,
      prefix: false,
      baseSite: false,
    });
    this.baseSub = this.baseSiteService
      .getActive()
      .pipe(take(1))
      .subscribe((res) => (this.baseSiteId = res));
    const allSolVax = [
      {
        id: 'operationalSol',
        name: 'Operational solutions',
        url: 'vax/en/USD/solutions/operational-solutions',
        description: 'A seamless suite of tools to improve efficiency',
        image: {
          url: 'icons/vaxServeAssests/Operational_Solutions.svg',
        },
        subcategories: [],
      },
      {
        id: 'dataSol',
        name: 'Data solutions',
        url: 'vax/en/USD/solutions/data-solutions',
        description: 'The ultimate business intelligence tool kit',
        image: {
          url: 'icons/vaxServeAssests/Data_solutions.svg',
        },
        subcategories: [],
      },
      {
        id: 'consultativeServices',
        name: 'Consultative services',
        url: 'vax/en/USD/solutions/consultative-solutions',
        description: 'Market support, program consultation & more',
        image: {
          url: 'icons/vaxServeAssests/Operational_Solutions.svg',
        },
        subcategories: [],
      },
      {
        id: 'costsavingsSol',
        name: 'Cost-savings solutions',
        url: 'vax/en/USD/solutions/cost-savings-solutions',
        description: 'The solutions deliver financial oversight that pays off.',
        image: {
          url: 'icons/vaxServeAssests/Cost-savings_solutions.svg',
        },
        subcategories: [],
      },
    ];

    if (this.baseSiteId === 'vsh') {
      this.customProdService.getCategory();
      this.categorySub = this.customProdService.category$.subscribe((res) => {
        if (res) {
          this.categoryDetails = res;
          this.categoryURL = this.categoryDetails.url;
          this.categoryList = res?.subcategories;

          const allProd = {
            id: 'allprod',
            name: 'All Products',
            url: this.categoryURL,
            description: this.categoryDetails?.description,
            image: {
              url: this.categoryDetails?.image?.url,
            },
            subcategories: [],
          };
          this.categoryList.unshift(allProd);
          this.cdr.detectChanges();
        }
      });
    } else {
      this.categoryList = allSolVax;
    }
  }
  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.flagSub?.unsubscribe();
    this.categorySub?.unsubscribe();
    this.baseSub?.unsubscribe();
  }
  navigate(url) {
    this.router.navigateByUrl(url);
  }

  getImage(data: any) {
    if (data) {
      if (data.url !== undefined) {
        if (this.baseSiteId === 'vsh') {
          return `${this.baseUrl}${data.url}`;
        } else {
          return '../../' + data.url;
        }
      } else {
        if (this.baseSiteId === 'vsh') {
          return (this.fallbackImage =
            'i18n-assets/sanofi_missing_product.jpg');
        } else {
          return (this.fallbackImage =
            'i18n-assets/sanofi_missing_product_vax.jpg');
        }
      }
    }
    return undefined;
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }
  navigateToPdp() {
    this.route.goByUrl('/Open-Catalogue/c/1');
  }
}
